// MST
import { types, flow, cast, detach } from 'mobx-state-tree';

// Interfaces
import {
  ICertificationWebpageGeneralProofApiNullResponse,
  ICertificationWebpageGeneralProofApiListResponse,
  ICertificationWebpageGeneralProofDeleteDocumentAttrs,
  ICertificationWebpageGeneralProofGetListAttrs,
  ICertificationWebpageGeneralProofUpdateAttrs,
  ICertificationWebpageGeneralProofUploadDocumentAttrs,
  ICertificationWEbpageGeneralProofApiUploadDocumentResponse,
  ICertificationWebpageGeneralProofDownloadDocumentAttrs,
  ICertificationWebpageGeneralProofApiDownloadDocumentResponse,
  ICertificationWebpageGeneralProofApiUpdateAttrs,
} from 'interfaces/webpage/certification/registration/precondition';
import {
  ISharedCommonFileAnswerAttrs,
  ISharedCommonLoadingAttrs,
  ISharedCommonLoadingQuestionSetAttrs,
  ISharedCommonUpdateAnswerAttrs,
} from 'interfaces/shared/shared-common.interface';

// Extensions
import { withEnvironment } from 'models/extensions/with-environment';
import {
  withHelper_commonMapQuestionSetLoading,
  withHelper_commonMapQuestionSet,
  withHelper_commonMapAnswerValue,
  withHelper_commonGetPreviousAnswerValue,
  withHelper_commonMapAnswerFiles,
} from 'models/extensions/helper/certification-registration.helper';
import { withHelper_fileDownload } from 'models/extensions/with-helper';
import {
  withHelper_questionSetModel,
} from 'models/extensions/helper/certification-registration-question-set.helper'
import {
  withHelper_commonLoadingModel,
  withHelper_commonMapLoading,
} from 'models/extensions/helper'

// Constant
import { ASSESSMENT_FEATURES, LOADING, LOADING_KEY_TYPE } from 'constant/shared.constant';

// Types
const { model, optional, array } = types;

// Loading
const { BASE, ACTION, DOWNLOAD } = LOADING;
const { ANSWER, ANSWER_FILES } = LOADING_KEY_TYPE;

const CertificationWebpageGeneralProofModel = model('CertificationWebpageGeneralProof')
  .props({
    certificationWebpageGeneralProof_loading: withHelper_commonLoadingModel,
    certificationWebpageGeneralProof_list: optional(array(withHelper_questionSetModel), []),
  })
  .actions(self => ({
    certificationWebpageGeneralProof_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.certificationWebpageGeneralProof_loading, payload);

      detach(self.certificationWebpageGeneralProof_loading);
      self.certificationWebpageGeneralProof_loading = cast(map);
    },
    certificationWebpageGeneralProof_SET_LIST: (
      payload: ICertificationWebpageGeneralProofApiListResponse['res']['data'],
    ): void => {
      self.certificationWebpageGeneralProof_list = cast(withHelper_commonMapQuestionSet(payload));
    },
    certificationWebpageGeneralProof_MAP_LOADING_LIST: (payload: ISharedCommonLoadingQuestionSetAttrs): void => {
      const map = withHelper_commonMapQuestionSetLoading(self.certificationWebpageGeneralProof_list, payload);

      detach(self.certificationWebpageGeneralProof_list);
      self.certificationWebpageGeneralProof_list = cast(map);
    },
    certificationWebpageGeneralProof_MAP_ANSWER_LIST: (payload: ISharedCommonUpdateAnswerAttrs): void => {
      const map = withHelper_commonMapAnswerValue(self.certificationWebpageGeneralProof_list, payload);

      detach(self.certificationWebpageGeneralProof_list);
      self.certificationWebpageGeneralProof_list = cast(map);
    },
    certificationWebpageGeneralProof_MAP_ANSWER_FILE_LIST: (payload: ISharedCommonFileAnswerAttrs): void => {
      const map = withHelper_commonMapAnswerFiles(self.certificationWebpageGeneralProof_list, payload);

      detach(self.certificationWebpageGeneralProof_list);
      self.certificationWebpageGeneralProof_list = cast(map);
    },
    certificationWebpageGeneralProof_CLEAR_LIST: (): void => {
      self.certificationWebpageGeneralProof_list = cast([]);
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    update: (
      payload: ICertificationWebpageGeneralProofApiUpdateAttrs,
    ): Promise<ICertificationWebpageGeneralProofApiNullResponse> => {
      return self.environment.certificationWebpageGeneralProofApi.certificationWebpageGeneralProof_update({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      });
    },
  }))
  .actions(self => ({
    /**
     * @description Fetch general proof list
     *
     * @param {ICertificationWebpageGeneralProofGetListAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageGeneralProof_fetchList: flow(function* (payload: ICertificationWebpageGeneralProofGetListAttrs) {
      self.certificationWebpageGeneralProof_SET_LOADING({ type: BASE, value: true });

      const response: ICertificationWebpageGeneralProofApiListResponse =
        yield self.environment.certificationWebpageGeneralProofApi.certificationWebpageGeneralProof_getList({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        });

      // Do when response success
      if (response.ok) {
        self.certificationWebpageGeneralProof_SET_LIST(response.res.data);

        if (payload?.onSuccess) payload.onSuccess();
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      self.certificationWebpageGeneralProof_SET_LOADING({ type: BASE, value: false });
    }),

    /**
     * @description Update general proof
     *
     * @param {ICertificationWebpageGeneralProofUpdateAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageGeneralProof_update: (payload: ICertificationWebpageGeneralProofUpdateAttrs) => {
      const { assessmentId, questionSetId, resultId, resultDetailId, currentValue, value } = payload.params;

      // Assign value by client-side
      self.certificationWebpageGeneralProof_MAP_ANSWER_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        currentValue,
        value,
        featureName: ASSESSMENT_FEATURES.GENERAL_PROOF,
        callbackResultDetailAnswerOptionIndex: async (_, data) => {
          // Start action loading
          self.certificationWebpageGeneralProof_SET_LOADING({ type: ACTION, value: true });

          const resultDetailIndex = data.findIndex(resultDetail => resultDetail.id === resultDetailId);
          const resultDetail = data[resultDetailIndex === 1 ? resultDetailIndex + 1 : resultDetailIndex - 1];

          // Start Loading
          self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
            questionSetId,
            resultId,
            resultDetailId,
            type: ANSWER,
            loadingType: ACTION,
            value: true,
          });

          const response: ICertificationWebpageGeneralProofApiNullResponse = await self.update(payload);

          // Do when response success
          if (response.ok) {
            if (payload?.onSuccess) payload.onSuccess();
          }

          // Do when response failed
          if (!response.ok) {
            // Revert value to previous value
            self.certificationWebpageGeneralProof_MAP_ANSWER_LIST({
              questionSetId,
              resultId,
              resultDetailId,
              currentValue,
              value: withHelper_commonGetPreviousAnswerValue(currentValue),
            });

            if (payload?.onError) payload.onError();
          }

          // End the loading
          self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
            questionSetId,
            resultId,
            resultDetailId,
            type: ANSWER,
            loadingType: ACTION,
            value: false,
          });

          // Start the other column loading
          self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
            questionSetId,
            resultId,
            resultDetailId: resultDetail.id!,
            type: ANSWER,
            loadingType: ACTION,
            value: true,
          });

          if (response.ok) {
            // Remap other column
            self.certificationWebpageGeneralProof_MAP_ANSWER_LIST({
              questionSetId,
              resultId,
              resultDetailId: resultDetail?.id!,
              currentValue: resultDetail,
              value: null,
            });

            // Re update other column
            const selfUpdateResponse: ICertificationWebpageGeneralProofApiNullResponse = await self.update({
              params: { assessmentId },
              body: {
                answerCode: resultDetail.answer?.answerCode!,
                value: null,
              },
              requestConfig: {},
            });

            // Do when response failed
            if (!selfUpdateResponse.ok) {
              // Revert value to previous value
              self.certificationWebpageGeneralProof_MAP_ANSWER_LIST({
                questionSetId,
                resultId,
                resultDetailId: resultDetail.id!,
                currentValue: resultDetail,
                value: withHelper_commonGetPreviousAnswerValue(currentValue),
              });
            }

            // End the other column loading
            self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
              questionSetId,
              resultId,
              resultDetailId: resultDetail.id!,
              type: ANSWER,
              loadingType: ACTION,
              value: true,
            });
          }

          // End action loading
          self.certificationWebpageGeneralProof_SET_LOADING({ type: ACTION, value: false });
        },
      });
    },

    /**
     * @description Upload general proof document
     *
     * @param {ICertificationWebpageGeneralProofUploadDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageGeneralProof_uploadDocument: flow(function* (
      payload: ICertificationWebpageGeneralProofUploadDocumentAttrs,
    ) {
      // Start Base Loading
      self.certificationWebpageGeneralProof_SET_LOADING({ type: ACTION, value: true });

      const { assessmentId, questionSetId, resultId, resultDetailId, fileId, value } = payload.params;

      // Start Loading
      self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: true,
      });

      const response: ICertificationWEbpageGeneralProofApiUploadDocumentResponse =
        yield self.environment.certificationWebpageGeneralProofApi.certificationWebpageGeneralProof_uploadDocument({
          params: { assessmentId },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        });

      // Do when response success
      if (response.ok) {
        // Map data when success
        self.certificationWebpageGeneralProof_MAP_ANSWER_FILE_LIST({
          questionSetId,
          resultId,
          resultDetailId,
          fileId: fileId!,
          value: { ...response.res.data, userOriginalFileName: value.name },
          type: 'edit',
        });

        if (payload?.onSuccess) payload.onSuccess();
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      // Stop Loading
      self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: false,
      });

      // End Base Loading
      self.certificationWebpageGeneralProof_SET_LOADING({ type: ACTION, value: false });
    }),

    /**
     * @description Delete general proof document
     *
     * @param {ICertificationWebpageGeneralProofDeleteDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageGeneralProof_deleteDocument: flow(function* (
      payload: ICertificationWebpageGeneralProofDeleteDocumentAttrs,
    ) {
      // Start Base Loading
      self.certificationWebpageGeneralProof_SET_LOADING({ type: ACTION, value: true });

      const { userFileTransactionId, questionSetId, resultId, resultDetailId, fileId } = payload.params;

      // Start Loading
      self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: true,
      });

      const response: ICertificationWebpageGeneralProofApiNullResponse =
        yield self.environment.certificationWebpageGeneralProofApi.certificationWebpageGeneralProof_deleteDocument({
          params: { userFileTransactionId },
          requestConfig: { ...payload.requestConfig },
        });

      // Do when response success
      if (response.ok) {
        // Map data when success
        self.certificationWebpageGeneralProof_MAP_ANSWER_FILE_LIST({
          questionSetId,
          resultId,
          resultDetailId,
          fileId: fileId!,
          type: 'delete',
        });

        if (payload?.onSuccess) payload.onSuccess();
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      // Stop Loading
      self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: ACTION,
        value: false,
      });

      // End Base Loading
      self.certificationWebpageGeneralProof_SET_LOADING({ type: ACTION, value: false });
    }),

    /**
     * @description Download document of general proof
     *
     * @param {ICertificationWebpageGeneralProofDownloadDocumentAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageGeneralProof_downloadDocument: flow(function* (
      payload: ICertificationWebpageGeneralProofDownloadDocumentAttrs,
    ) {
      const { blobId, questionSetId, resultId, resultDetailId, fileId } = payload.params;

      // Start Loading
      self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: true,
      });

      const response: ICertificationWebpageGeneralProofApiDownloadDocumentResponse =
        yield self.environment.certificationWebpageGeneralProofApi.certificationWebpageGeneralProof_downloadDocument({
          params: { blobId },
          requestConfig: { ...payload.requestConfig },
        });

      // Do when response success
      if (response.ok) {
        console.log(response, 'ini general download resp');
        withHelper_fileDownload(response.res, response.headers!);

        if (payload?.onSuccess) payload.onSuccess();
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError();
      }

      // Stop Loading
      self.certificationWebpageGeneralProof_MAP_LOADING_LIST({
        questionSetId,
        resultId,
        resultDetailId,
        fileId,
        type: ANSWER_FILES,
        loadingType: DOWNLOAD,
        value: false,
      });
    }),
  }));

export const createCertificationWebpageGeneralProofDefaultModel = () => optional(CertificationWebpageGeneralProofModel, {});
